import { getSurveyResponses } from './survey.service';
import type {
  SurveyResponse,
  AnalyticsMetrics,
  FilterState,
  Rating,
} from '@/types';

/** Max value on the 1–5 satisfaction scale (single question). */
const MAX_RATING = 5;

/** All Likert fields in `ratings` — must stay in sync with `SatisfactionRatings` (11 fields). */
const ALL_RATING_FIELDS: (keyof SurveyResponse['ratings'])[] = [
  'productQuality_expectationsMet',
  'productQuality_performanceAsExpected',
  'delivery_timeliness',
  'delivery_processEfficient',
  'communication_clearTimely',
  'communication_promptResponses',
  'staff_professionalCourteousHelpful',
  'staff_supportAdequateEffective',
  'price_valueForMoney',
  'overall_overallSatisfied',
  'overall_wouldRecommend',
];

const NUM_RATING_FIELDS = ALL_RATING_FIELDS.length;
/** Maximum possible sum for one response: 5 × number of rating questions. */
const MAX_TOTAL_SCORE = MAX_RATING * NUM_RATING_FIELDS;

const sumResponseRatings = (response: SurveyResponse): number =>
  ALL_RATING_FIELDS.reduce((acc, key) => acc + (response.ratings[key] ?? 0), 0);

// Calculate category averages
const calculateCategoryAverage = (
  responses: SurveyResponse[],
  fieldNames: (keyof SurveyResponse['ratings'])[]
): number => {
  if (responses.length === 0) return 0;

  const sum = responses.reduce((acc, response) => {
    const categorySum = fieldNames.reduce((catAcc, fieldName) => {
      return catAcc + (response.ratings[fieldName] || 0);
    }, 0);
    return acc + categorySum / fieldNames.length;
  }, 0);

  return Number((sum / responses.length).toFixed(2));
};

/**
 * CSAT (%) = (average score ÷ total possible score) × 100
 * - Average score = (sum of all rating values across all questions and responses) ÷ number of customers
 * - Total possible score = 5 × 11 (max rating × number of rating fields)
 */
const calculateCSATPercentage = (responses: SurveyResponse[]): number => {
  if (responses.length === 0) return 0;

  const sumAllRatings = responses.reduce((acc, r) => acc + sumResponseRatings(r), 0);
  const averageScore = sumAllRatings / responses.length;

  return Number(((averageScore / MAX_TOTAL_SCORE) * 100).toFixed(2));
};

/**
 * Would Recommend (%) = (average of `overall_wouldRecommend` ÷ 5) × 100
 * Interprets “Average Would Recommend × 100” as percentage on the 1–5 scale (same as avg × 20).
 */
const calculateRecommendationPercentage = (
  responses: SurveyResponse[]
): number => {
  if (responses.length === 0) return 0;

  const sum = responses.reduce(
    (acc, r) => acc + r.ratings.overall_wouldRecommend,
    0
  );
  const average = sum / responses.length;

  return Number(((average / MAX_RATING) * 100).toFixed(2));
};

// Get analytics metrics for filtered responses
export const getAnalyticsMetrics = async (
  filters?: FilterState
): Promise<AnalyticsMetrics> => {
  const responses = await getSurveyResponses(filters);

  const productQualityFields: (keyof SurveyResponse['ratings'])[] = [
    'productQuality_expectationsMet',
    'productQuality_performanceAsExpected',
  ];

  const deliveryFields: (keyof SurveyResponse['ratings'])[] = [
    'delivery_timeliness',
    'delivery_processEfficient',
  ];

  const communicationFields: (keyof SurveyResponse['ratings'])[] = [
    'communication_clearTimely',
    'communication_promptResponses',
  ];

  const staffFields: (keyof SurveyResponse['ratings'])[] = [
    'staff_professionalCourteousHelpful',
    'staff_supportAdequateEffective',
  ];

  const priceFields: (keyof SurveyResponse['ratings'])[] = [
    'price_valueForMoney',
  ];

  const overallFields: (keyof SurveyResponse['ratings'])[] = [
    'overall_overallSatisfied',
    'overall_wouldRecommend',
  ];

  return {
    categoryAverages: {
      productQuality: calculateCategoryAverage(responses, productQualityFields),
      delivery: calculateCategoryAverage(responses, deliveryFields),
      communication: calculateCategoryAverage(responses, communicationFields),
      staff: calculateCategoryAverage(responses, staffFields),
      price: calculateCategoryAverage(responses, priceFields),
      overall: calculateCategoryAverage(responses, overallFields),
    },
    csatPercentage: calculateCSATPercentage(responses),
    recommendationPercentage: calculateRecommendationPercentage(responses),
    totalResponses: responses.length,
    dateRange: filters?.dateRange || {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      end: new Date().toISOString(),
    },
  };
};

// Get trend data for time series charts
export const getTrendData = async (
  filters?: FilterState,
  groupBy: 'day' | 'week' | 'month' = 'day'
): Promise<Array<{ date: string; value: number; label: string }>> => {
  const responses = await getSurveyResponses(filters);

  // Group responses by date
  const grouped = new Map<string, SurveyResponse[]>();

  responses.forEach((response) => {
    const date = new Date(response.createdAt);
    let key: string;

    if (groupBy === 'day') {
      key = date.toISOString().split('T')[0]!;
    } else if (groupBy === 'week') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      key = weekStart.toISOString().split('T')[0]!;
    } else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(response);
  });

  // CSAT % per period (same formula as KPI)
  return Array.from(grouped.entries())
    .map(([date, groupResponses]) => {
      const sumScores = groupResponses.reduce(
        (acc, r) => acc + sumResponseRatings(r),
        0
      );
      const averageScore = sumScores / groupResponses.length;
      const csatPct = (averageScore / MAX_TOTAL_SCORE) * 100;

      return {
        date,
        value: Number(csatPct.toFixed(2)),
        label: date,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

// Get distribution data for ratings
export const getRatingDistribution = async (
  filters?: FilterState,
  category: keyof SurveyResponse['ratings'] = 'overall_overallSatisfied'
): Promise<Array<{ rating: Rating; count: number; percentage: number }>> => {
  const responses = await getSurveyResponses(filters);
  const total = responses.length;

  if (total === 0) {
    return [1, 2, 3, 4, 5].map((r) => ({
      rating: r as Rating,
      count: 0,
      percentage: 0,
    }));
  }

  const distribution = new Map<Rating, number>();

  responses.forEach((response) => {
    const rating = response.ratings[category];
    distribution.set(rating, (distribution.get(rating) || 0) + 1);
  });

  return [1, 2, 3, 4, 5].map((r) => {
    const rating = r as Rating;
    const count = distribution.get(rating) || 0;
    return {
      rating,
      count,
      percentage: Number(((count / total) * 100).toFixed(2)),
    };
  });
};

